import React, { useState, useEffect, useCallback } from 'react';
import { assignmentsApi, complaintsApi, servicesApi, staffApi } from '../api/client';
import { Badge, Spin, Empty, Modal, F, useFlash, parseErr } from '../components/UI';
import { Edit } from '../components/Icons';
import { useAuth } from '../context/AuthContext';

const fmtDate = d => d ? new Date(d + 'T00:00:00').toLocaleDateString('en-IN', { dateStyle: 'medium' }) : '—';
const TYPE_COLOR = { complaint: 'rgba(220,53,69,0.08)', service: 'rgba(29,78,216,0.08)' };
const TYPE_TEXT  = { complaint: 'var(--red)', service: 'var(--navy)' };
const COMP_STATUSES = ['open','in_progress','resolved','closed'];
const SRV_STATUSES  = ['pending','in_progress','completed','cancelled'];

const sel = { height:30, border:'1px solid var(--border)', borderRadius:6, fontSize:12, padding:'0 6px', background:'var(--surface)' };

export default function Assignments() {
  const { canWrite } = useAuth();
  const [rows, setRows]       = useState([]);
  const [busy, setBusy]       = useState(true);
  const [staff, setStaff]     = useState([]);
  const [typeF, setTypeF]     = useState('');
  const [statusF, setStatusF] = useState('');
  const { show, Flash }       = useFlash();

  // Edit modal
  const [editing, setEditing]   = useState(null);
  const [editForm, setEditForm] = useState({ status:'', assigned_to:'', schedule_date:'' });
  const [editSaving, setES]     = useState(false);
  const setEF = k => e => setEditForm(f => ({ ...f, [k]: e.target.value }));

  const load = useCallback(async () => {
    setBusy(true);
    try { const { data } = await assignmentsApi.list(); setRows(data); }
    finally { setBusy(false); }
  }, []);

  useEffect(() => { load(); staffApi.list().then(({ data }) => setStaff(data)).catch(() => {}); }, [load]);

  const openEdit = r => {
    setEditing(r);
    setEditForm({ status: r.status, assigned_to: r.assigned_to_id || '', schedule_date: r.schedule_date || '' });
  };

  const saveEdit = async () => {
    setES(true);
    try {
      const payload = {
        status: editForm.status,
        assigned_to: editForm.assigned_to || null,
        ...(editing.type === 'complaint' ? { preferred_date: editForm.schedule_date || null } : { scheduled_date: editForm.schedule_date || null }),
      };
      if (editing.type === 'complaint') await complaintsApi.update(editing.id, payload);
      else await servicesApi.update(editing.id, payload);
      show('Assignment updated!');
      setEditing(null);
      load();
    } catch(e) { show(parseErr(e, 'Failed to update.'), 'error'); }
    finally { setES(false); }
  };

  const filtered = rows.filter(r => {
    if (typeF   && r.type   !== typeF)   return false;
    if (statusF && r.status !== statusF) return false;
    return true;
  });

  if (busy) return <Spin/>;

  return (
    <div>
      <Flash/>
      <div className="page-hd"><h2>Field Assignments</h2></div>

      <div className="stat-grid" style={{ gridTemplateColumns: 'repeat(4,1fr)' }}>
        <div className="stat-card"><div className="stat-lbl">Total Active</div><div className="stat-val">{rows.length}</div></div>
        <div className="stat-card"><div className="stat-lbl">Complaints</div><div className="stat-val" style={{ color:'var(--red)' }}>{rows.filter(r=>r.type==='complaint').length}</div></div>
        <div className="stat-card"><div className="stat-lbl">Service</div><div className="stat-val" style={{ color:'var(--navy)' }}>{rows.filter(r=>r.type==='service').length}</div></div>
        <div className="stat-card"><div className="stat-lbl">In Progress</div><div className="stat-val" style={{ color:'orange' }}>{rows.filter(r=>r.status==='in_progress').length}</div></div>
      </div>

      {/* Compact filters */}
      <div style={{ display:'flex', gap:6, alignItems:'center', marginBottom:12 }}>
        <select value={typeF} onChange={e=>setTypeF(e.target.value)} style={sel}>
          <option value="">All Types</option>
          <option value="complaint">Complaints</option>
          <option value="service">Service</option>
        </select>
        <select value={statusF} onChange={e=>setStatusF(e.target.value)} style={sel}>
          <option value="">All Statuses</option>
          <option value="open">Open</option>
          <option value="pending">Pending</option>
          <option value="in_progress">In Progress</option>
        </select>
        {(typeF||statusF) && <button className="btn btn-sm" style={{ height:30, fontSize:12 }} onClick={()=>{setTypeF('');setStatusF('');}}>Clear</button>}
      </div>

      <div className="card card-0">
        {filtered.length === 0
          ? <Empty msg="No active assignments"/>
          : <div className="tbl-wrap"><table>
              <thead><tr>
                <th>Type</th><th>Ref #</th><th>Customer</th><th>Assigned To</th>
                <th>Status</th><th>Priority</th><th>Scheduled</th>
                {canWrite && <th>Actions</th>}
              </tr></thead>
              <tbody>
                {filtered.map(r => (
                  <tr key={r.id}>
                    <td>
                      <span style={{ fontSize:11, fontWeight:600, color:TYPE_TEXT[r.type], background:TYPE_COLOR[r.type], borderRadius:4, padding:'2px 7px' }}>
                        {r.type === 'complaint' ? 'Complaint' : 'Service'}
                      </span>
                    </td>
                    <td style={{ fontFamily:'monospace', fontSize:12, fontWeight:600 }}>{r.ref_code}</td>
                    <td style={{ fontWeight:500 }}>{r.customer_name}</td>
                    <td>
                      {r.assigned_to_name
                        ? <span style={{ display:'inline-flex', alignItems:'center', gap:5 }}>
                            <span style={{ width:22, height:22, borderRadius:'50%', background:'var(--navy)', color:'#fff', fontSize:10, fontWeight:700, display:'inline-flex', alignItems:'center', justifyContent:'center' }}>
                              {r.assigned_to_name.split(' ').map(w=>w[0]).slice(0,2).join('').toUpperCase()}
                            </span>
                            {r.assigned_to_name}
                          </span>
                        : <span style={{ color:'var(--text2)', fontSize:12 }}>Unassigned</span>
                      }
                    </td>
                    <td><Badge label={r.status}/></td>
                    <td>{r.priority ? <Badge label={r.priority}/> : '—'}</td>
                    <td style={{ fontSize:12, color:'var(--text2)' }}>{fmtDate(r.schedule_date)}</td>
                    {canWrite && <td><button className="btn btn-sm" onClick={()=>openEdit(r)}><Edit s={13}/></button></td>}
                  </tr>
                ))}
              </tbody>
            </table></div>
        }
      </div>

      {/* Edit Modal */}
      <Modal open={!!editing} onClose={()=>setEditing(null)} title={`Edit — ${editing?.ref_code||''}`}
        footer={<><button className="btn" onClick={()=>setEditing(null)}>Cancel</button><button className="btn btn-primary" onClick={saveEdit} disabled={editSaving}>{editSaving?'Saving…':'Save'}</button></>}
      >
        {editing && <div className="fg-2">
          <F label="Status" req><select value={editForm.status} onChange={setEF('status')}>
            {(editing.type==='complaint' ? COMP_STATUSES : SRV_STATUSES).map(s=><option key={s} value={s}>{s.replace('_',' ')}</option>)}
          </select></F>
          <F label="Assigned To"><select value={editForm.assigned_to} onChange={setEF('assigned_to')}>
            <option value="">— Unassigned —</option>
            {staff.filter(s=>s.is_active).map(s=><option key={s.id} value={s.id}>{s.name} ({s.role})</option>)}
          </select></F>
          <F label="Scheduled Date" full><input type="date" value={editForm.schedule_date} onChange={setEF('schedule_date')}/></F>
        </div>}
      </Modal>
    </div>
  );
}
